<template>
  <div class="places-autocomplete-input">
    <MultiSelect
      class="places-autocomplete-input__select"
      v-model="modelValue"
      :aria-labelledby="ariaLabelledby"
      :clearable="true"
      :custom-label="customLabel"
      :internal-search="false"
      :options="options"
      :placeholder="placeholder"
      :searchable="true"
      :show-no-options="false"
      :show-no-results="false"
      @remove="onRemove"
      @search-change="debouncedAutocompleteRequest"
      @select="(option) => onSelect(option)"
    >
      <template #option="props">
        <span v-html="highlightPhrase(props.option, props.search)"></span>
      </template>
    </MultiSelect>
  </div>
</template>

<script lang="ts">
  import { getThemeText } from "Constants/themeVars";
  import {
    configureGoogleMaps,
    importLibrary,
  } from "Services/google-maps-loader";
  import type { TPlaceResult } from "Services/types/counselor-grid-service-types";
  import debounce from "lodash-es/debounce";
  /* global google */
  import { defineComponent, markRaw } from "vue";
  import MultiSelect from "vue-multiselect";
  import { mapMutations, mapState } from "vuex";

  const DIGIT_REGEX = /\d/;
  const WORD_SEPARATOR_REGEX = /[\s,]+/;
  // Leading numeric postal code such as "624 34 " or "624 34, " (SE/NO/DK style)
  const LEADING_POSTAL_CODE_REGEX = /^\s*\d{2,}[\d\s]*,?\s*/;
  const POSTAL_CODE_TEXT_REGEX = /^\s*\d{2,}[\d\s]/;
  const REGEX_METACHARACTERS = /[.*+?^${}()|[\]\\]/g;

  export default defineComponent({
    components: {
      MultiSelect,
    },

    computed: {
      ...mapState("counselorGrid", ["googleMapsIsAvailable", "filteredPlace"]),
      ...mapState("users", ["filteredByPrompt"]),
    },

    data() {
      const currentSite = window.theme_vars.currentSite.toLowerCase() || "se";
      const locale = window.theme_vars.locale || "sv";

      return {
        currentSite: window.theme_vars.currentSite,
        latestSelectionId: 0,
        loading: false,
        modelValue: null,
        newestRequestId: 0,
        options: [],
        request: {
          includedPrimaryTypes: [
            "locality",
            "postal_town",
            "postal_code",
            "sublocality",
            "colloquial_area",
          ],
          includedRegionCodes: [currentSite],
          input: "",
          language: locale,
          region: currentSite,
          sessionToken:
            null as google.maps.places.AutocompleteSessionToken | null,
        },
        searchString: "",
        suggestions: [],
      };
    },

    methods: {
      ...mapMutations("counselorGrid", ["setGoogleMapsIsAvailable"]),

      // Only request postal-code predictions when the query contains a digit,
      // so a name search like "Gotland" can't resolve to a postal address.
      buildIncludedPrimaryTypes(searchQuery: string): string[] {
        const base = [
          "locality",
          "postal_town",
          "sublocality",
          "colloquial_area",
        ];

        return DIGIT_REGEX.test(searchQuery) ? [...base, "postal_code"] : base;
      },

      calculateLevenshteinSimilarity(str1: string, str2: string): number {
        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;

        if (longer.length === 0) {
          return 1.0;
        }

        const distance = this.levenshteinDistance(longer, shorter);

        return (longer.length - distance) / longer.length;
      },

      calculateMatchScore(
        placePrediction: google.maps.places.PlacePrediction,
        normalizedSearch: string
      ): number {
        const text = placePrediction.text?.text?.toLowerCase() || "";

        if (!text) {
          return 0;
        }

        let score: number;

        // Exact match gets highest priority
        if (text === normalizedSearch) {
          score = 1000;
        } else if (
          text.split(WORD_SEPARATOR_REGEX).includes(normalizedSearch)
        ) {
          // Exact word match gets high priority
          score = 500;
        } else if (text.startsWith(normalizedSearch)) {
          // Starts with search gets medium priority
          score = 100;
        } else if (text.includes(normalizedSearch)) {
          // Contains search gets low priority
          score = 10;
        } else {
          // Fuzzy match as fallback
          score = this.calculateLevenshteinSimilarity(text, normalizedSearch);
        }

        // For a non-numeric query, demote postal-code predictions so a real
        // locality wins the tie — but only slightly, so a postal code still
        // beats a non-matching suggestion. Type isn't always populated, so also
        // detect a leading-digits text pattern.
        const isPostalCode =
          placePrediction.types?.includes("postal_code") ||
          POSTAL_CODE_TEXT_REGEX.test(text);
        if (isPostalCode && !DIGIT_REGEX.test(normalizedSearch)) {
          score -= 50;
        }

        return score;
      },

      customLabel(option: {
        text?: google.maps.places.FormattableText | string;
      }) {
        const text = option?.text;

        if (typeof text === "string") {
          return text;
        }

        return text?.text ?? "";
      },

      debouncedAutocompleteRequest: debounce(async function (
        searchQuery: string
      ) {
        if (searchQuery.length < 2) {
          return;
        }

        await this.makeAutocompleteRequest(searchQuery, true);
      }, 500),

      findBestPlaceMatch(
        suggestions: google.maps.places.AutocompleteSuggestion[],
        searchQuery: string
      ): google.maps.places.AutocompleteSuggestion | null {
        if (!suggestions.length) {
          return null;
        }

        const normalizedSearch = searchQuery.trim().toLowerCase();
        let bestMatch: google.maps.places.AutocompleteSuggestion | null = null;
        let bestScore = -1;

        for (const suggestion of suggestions) {
          const { placePrediction } = suggestion;
          if (!placePrediction?.text) {
            continue;
          }

          const score = this.calculateMatchScore(
            placePrediction,
            normalizedSearch
          );
          if (score > bestScore) {
            bestScore = score;
            bestMatch = suggestion;
          }
        }

        return bestMatch || suggestions[0];
      },

      highlightPhrase(
        placePrediction: google.maps.places.PlacePrediction,
        search: string
      ) {
        const text = placePrediction?.text?.text;

        if (!text) {
          return "";
        }

        if (!search) {
          return text;
        }

        // Escape regex metacharacters — raw user input would crash on "(", "[", etc.
        const escaped = search.replace(REGEX_METACHARACTERS, "\\$&");
        const searchRegex = new RegExp(`(${escaped})`, "gi");
        return text.replace(searchRegex, "<strong>$1</strong>");
      },

      async initPlacesAutocomplete(): Promise<void> {
        configureGoogleMaps();

        let googleMapsIsAvailable = false;

        try {
          await importLibrary("places");

          const places = google.maps?.places;

          googleMapsIsAvailable = places !== undefined && places !== null;
        } catch (error) {
          console.error("Failed to initialize Google Maps:", error);
          googleMapsIsAvailable = false;
        }

        await this.setGoogleMapsIsAvailable(googleMapsIsAvailable);

        if (!googleMapsIsAvailable) {
          return;
        }

        await this.refreshToken();
        await this.maybeSetPreselectedCity();
      },

      levenshteinDistance(str1: string, str2: string): number {
        const matrix = new Array(str2.length + 1)
          .fill(null)
          .map(() => new Array(str1.length + 1).fill(null));

        for (let i = 0; i <= str1.length; i += 1) {
          matrix[0][i] = i;
        }

        for (let j = 0; j <= str2.length; j += 1) {
          matrix[j][0] = j;
        }

        for (let j = 1; j <= str2.length; j += 1) {
          for (let i = 1; i <= str1.length; i += 1) {
            const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
            matrix[j][i] = Math.min(
              matrix[j][i - 1] + 1, // deletion
              matrix[j - 1][i] + 1, // insertion
              matrix[j - 1][i - 1] + cost // substitution
            );
          }
        }

        return matrix[str2.length][str1.length];
      },

      async makeAutocompleteRequest(
        searchQuery: string,
        populateOptions = true
      ) {
        if (!searchQuery) {
          return;
        }

        this.loading = true;
        this.request.input = searchQuery;
        this.request.includedPrimaryTypes =
          this.buildIncludedPrimaryTypes(searchQuery);
        this.searchString = searchQuery;
        this.newestRequestId += 1;
        const requestId = this.newestRequestId;

        if (!this.googleMapsIsAvailable) {
          console.error("Google Maps is not initialized");
          this.loading = false;

          return;
        }

        const { AutocompleteSuggestion } = await importLibrary("places");
        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(
            this.request
          );

        if (requestId !== this.newestRequestId) {
          return;
        }

        if (!populateOptions) {
          if (!suggestions.length) {
            this.modelValue = null;
            this.loading = false;
            return;
          }

          const bestMatch = this.findBestPlaceMatch(suggestions, searchQuery);
          const prediction =
            bestMatch?.placePrediction || suggestions[0].placePrediction;
          this.modelValue = markRaw(prediction);
          this.onSelect(this.modelValue, true);
          this.loading = false;
          return;
        }

        this.options = suggestions.map(
          (suggestion: google.maps.places.AutocompleteSuggestion) =>
            markRaw(suggestion.placePrediction)
        );

        this.loading = false;
      },

      async maybeSetPreselectedCity() {
        if (!this.preselectedCity) {
          return;
        }

        // Use curated coordinates when supplied; otherwise geocode the name.
        if (this.setPreselectedFromCoordinates()) {
          return;
        }

        await this.makeAutocompleteRequest(this.preselectedCity, false);
      },

      onRemove() {
        this.modelValue = null;

        this.$emit("place-removed", {
          location: undefined,
          name: "",
        } as TPlaceResult);
      },

      async onSelect(
        placePrediction: google.maps.places.PlacePrediction,
        preselected = false
      ) {
        // Warning: Use only Essentials fields to avoid Enterprise + Atmosphere billing
        // https://developers.google.com/maps/billing-and-pricing/sku-details#place-details-ess-sku
        this.latestSelectionId += 1;
        const selectionId = this.latestSelectionId;

        try {
          const selectedPlace = await placePrediction.toPlace();

          // A newer selection has started — drop this stale resolution.
          if (selectionId !== this.latestSelectionId) {
            return;
          }

          const { place } = await selectedPlace.fetchFields({
            fields: ["location", "formattedAddress"],
          });

          if (selectionId !== this.latestSelectionId) {
            return;
          }

          this.$emit("place-selected", {
            location: place.location,
            name: this.resolvePlaceName(place, placePrediction),
            preselected,
          } as TPlaceResult);
        } catch (error) {
          console.error("Failed to fetch place details:", error);
        }
      },

      async refreshToken() {
        if (!this.googleMapsIsAvailable) {
          console.error("Google Maps is not initialized for token refresh");
          return;
        }

        const { AutocompleteSessionToken } = await importLibrary("places");

        this.request.sessionToken = new AutocompleteSessionToken();
      },

      // Prefer the prediction label over formattedAddress, which can carry a
      // postal code even for a locality; strip any leading postal code, and
      // fall back to secondaryText if that leaves nothing.
      resolvePlaceName(
        place: google.maps.places.Place,
        placePrediction: google.maps.places.PlacePrediction
      ): string {
        const predictionText = placePrediction.text?.text?.trim() || "";
        const secondaryText = placePrediction.secondaryText?.text?.trim() || "";
        const formattedAddress = place.formattedAddress?.trim() || "";

        const candidate = predictionText || formattedAddress;

        // Only strip a leading postal code from an address or a postal-code
        // prediction — never from a real label like "10e Arrondissement".
        const shouldStripPostalCode =
          !predictionText || placePrediction.types?.includes("postal_code");
        const resolved = shouldStripPostalCode
          ? this.stripLeadingPostalCode(candidate)
          : candidate;

        return resolved || secondaryText || candidate;
      },

      setPreselectedFromCoordinates(): boolean {
        const coords = this.preselectedCoordinates;

        if (
          !(
            coords &&
            Number.isFinite(coords.lat) &&
            Number.isFinite(coords.lng)
          ) ||
          coords.lat < -90 ||
          coords.lat > 90 ||
          coords.lng < -180 ||
          coords.lng > 180
        ) {
          return false;
        }

        const { lat, lng } = coords;

        this.modelValue = { text: this.preselectedCity };
        this.$emit("place-selected", {
          location: { lat: () => lat, lng: () => lng },
          name: this.preselectedCity,
          preselected: true,
        } as TPlaceResult);

        return true;
      },

      stripLeadingPostalCode(value: string): string {
        return value.replace(LEADING_POSTAL_CODE_REGEX, "").trim();
      },
    },

    mounted() {
      this.initPlacesAutocomplete();
    },
    name: "PlacesAutocompleteInput",

    props: {
      ariaLabelledby: {
        default: "",
        type: String,
      },
      placeholder: {
        default: getThemeText("counselorGridTexts.startTyping", ""),
        type: String,
      },
      preselectedCity: {
        default: "",
        type: String,
      },
      // Curated coordinates for the preselected city; when set we center on
      // these instead of geocoding the name (avoids region centroids).
      preselectedCoordinates: {
        default: null,
        type: Object as () => { lat: number; lng: number } | null,
      },
    },

    watch: {
      filteredByPrompt() {
        if (!this.filteredPlace) {
          this.modelValue = null;
          this.options = [];

          return;
        }

        this.modelValue = { text: this.filteredPlace.name };
        this.options = [this.filteredPlace];
      },
    },
  });
</script>
